import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Archive, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Workspaces() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", type: "department" });
  const [submitting, setSubmitting] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // workspace sendo editado
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/workspaces");
      setItems(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const canEdit = (w) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return Array.isArray(w?.manager_ids) && w.manager_ids.includes(user.id);
  };

  const openEdit = (w) => {
    setEditTarget(w);
    setEditForm({ name: w.name || "", description: w.description || "" });
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm({ name: "", description: "" });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    const trimmedName = (editForm.name || "").trim();
    if (!trimmedName) {
      toast.error("Nome não pode ficar vazio.");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/workspaces/${editTarget.id}`, {
        name: trimmedName,
        description: (editForm.description || "").trim(),
      });
      // Atualiza somente o item alterado, sem recarregar toda a lista.
      setItems((prev) => Array.isArray(prev)
        ? prev.map((w) => (w.id === editTarget.id ? { ...w, ...data } : w))
        : prev);
      toast.success("Espaço atualizado.");
      closeEdit();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/workspaces", form);
      toast.success("Espaço criado.");
      setShowCreate(false);
      setForm({ name: "", description: "", type: "department" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="workspaces-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Estrutura</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Espaços de trabalho</h1>
          <p className="text-slate-500 mt-1">Cada espaço representa um departamento.</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={() => setShowCreate(true)} data-testid="workspaces-create-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
            <Plus className="w-4 h-4" /> Novo espaço
          </button>
        )}
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {items === null ? (
        <div className="text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="workspaces-empty">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />
          <h3 className="font-heading text-lg font-semibold mt-3">Nenhum espaço acessível</h3>
          <p className="text-sm text-slate-500 mt-1">
            {user?.role === "admin"
              ? "Crie o primeiro espaço para começar."
              : "Você ainda não foi adicionado a nenhum espaço. Fale com seu administrador."}
          </p>
        </div>
      ) : (
        <div data-testid="workspaces-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((w) => (
            <div key={w.id} className="relative">
              <Link
                to={`/espacos/${w.id}`}
                data-testid="workspace-card"
                className="block bg-white rounded-xl border border-slate-200 shadow-sm p-6 transition-all hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5"
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center">
                    <Building2 className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
                  </div>
                  {w.status === "archived" && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                      <Archive className="w-3 h-3" /> Arquivado
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold tracking-tight mt-4 pr-8">{w.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{w.type === "interdepartmental" ? "Interdepartamental" : "Departamental"}</p>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{w.description || "Sem descrição."}</p>
                <div className="mt-4 text-xs text-slate-500">{w.members_count} {w.members_count === 1 ? "membro" : "membros"}</div>
              </Link>
              {canEdit(w) && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(w); }}
                  title="Editar espaço"
                  aria-label={`Editar espaço ${w.name}`}
                  data-testid={`edit-workspace-${w.id}`}
                  className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.75} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" data-testid="create-workspace-modal" onClick={() => setShowCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-lg p-6 space-y-4">
            <h2 className="font-heading text-xl font-semibold tracking-tight">Novo espaço de trabalho</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-workspace-name"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo de espaço</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="new-workspace-type"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                <option value="department">Departamental (um gerente único)</option>
                <option value="interdepartmental">Interdepartamental (vários gerentes participantes)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Descrição</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="new-workspace-description"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={submitting} data-testid="new-workspace-submit"
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                {submitting ? "Criando..." : "Criar"}
              </button>
            </div>
          </form>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" data-testid="edit-workspace-modal" onClick={closeEdit}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitEdit}
            className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-lg p-6 space-y-4">
            <h2 className="font-heading text-xl font-semibold tracking-tight">Editar espaço de trabalho</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome</label>
              <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-workspace-name"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Descrição</label>
              <textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                data-testid="edit-workspace-description"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeEdit} data-testid="edit-workspace-cancel"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={savingEdit} data-testid="edit-workspace-submit"
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                {savingEdit ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
